import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import { UserIdManager } from '@/lib/utils/user-id';
import { Locale } from '@/types/locale';

import { useUserTourState } from './use-user-tour-state';

interface UseUserTourWithUserIdProps {
  dealId: string;
  dealName?: string | null;
  dealState?: string | null;
  editionMode?: string | null;
  page: 'brand' | 'choose';
  brandId?: string | null;
}

export function useUserTourWithUserId({
  dealId,
  dealName,
  dealState,
  editionMode,
  page,
  brandId,
}: UseUserTourWithUserIdProps) {
  const { i18n } = useTranslation();
  const user = useUser();
  const isInternalUser = user?.isInternalUser ?? false;
  const isOfferLocked = useIsOfferLocked();
  const { identify, group, isInitialized } = useUserTourState();

  useEffect(() => {
    if (isInternalUser) return;
    if (!isInitialized || !dealId || !dealName || isOfferLocked) return;

    // Récupérer ou générer le userId unique
    const userId = UserIdManager.getUserId();
    const companyId = `brand-company-${brandId || dealId}`;

    // Identifier l'utilisateur avec son userId unique
    identify(userId, {
      role: page,
      brandId: brandId || undefined,
      dealId: dealId,
      dealName: dealName,
      dealState: dealState || undefined,
      editionMode: editionMode || undefined,
      locale: i18n.language === 'en' ? Locale.US : Locale.FR,
      page: page,
    });

    // Associer le groupe (marque)
    group(companyId, {
      name: `${page === 'brand' ? 'Brand' : 'Choose'} ${brandId || dealId}`,
      role: page,
      dealId: dealId,
      dealName: dealName,
    });
  }, [
    // i18n.language (not i18n) so the effect re-runs and re-identifies with the
    // new locale when the user switches language — the i18n instance is stable.
    i18n.language,
    isInitialized,
    identify,
    group,
    dealId,
    dealName,
    dealState,
    editionMode,
    page,
    brandId,
    isInternalUser,
    isOfferLocked,
  ]);

  return {
    isInitialized,
  };
}
